<template>
  <main class="view noselect">
    <!-- Invisible click/touch zones: left 25% → prev, right 75% → next (same split as Project) -->
    <nav
      class="slidenavigation"
      @touchstart.prevent="onTouchStart"
      @touchend.prevent="onTouchEnd"
    >
      <div class="left button" @click.stop="goPrev"></div>
      <div class="right button" @click.stop="goNext"></div>
    </nav>

    <!-- Full-screen image stage -->
    <div class="stage home-stage">
      <ul class="slides">
        <li
          v-for="(project, pi) in projects"
          :key="project.uid"
          class="slide"
          v-show="pi === currentProjectIdx"
        >
          <!-- Slides loaded: show the current slide within this project -->
          <template v-if="projectSlides(project.uid).length">
            <div
              v-for="(slide, si) in projectSlides(project.uid)"
              :key="si"
              class="home-slide"
              v-show="si === currentSlideIdx"
            >
              <img
                v-for="file in slide.files"
                :key="file.filename"
                :src="file.url"
                :srcset="file.srcset"
                sizes="90vw"
              />
            </div>
          </template>
          <!-- Fallback: cover image while project data is loading -->
          <img
            v-else-if="project.cover"
            :src="project.cover.url"
            :srcset="project.cover.srcset"
            sizes="90vw"
          />
        </li>
      </ul>
    </div>

    <!-- Project title: bottom left -->
    <p v-if="currentProject" class="home-title">{{ currentProject.title }}</p>

    <!-- Slide counter: bottom right, only when the current project has multiple slides -->
    <p v-if="totalSlides > 1" class="home-counter">
      {{ currentSlideIdx + 1 }}&nbsp;&nbsp;/&nbsp;&nbsp;{{ totalSlides }}
    </p>
  </main>
</template>

<script setup>
import axios from 'axios';
import { useStore } from '@/KQL.js';

const kql = useStore();

// All projects newest-first (same order as the index)
const projects = computed(() => Object.values(kql.projects || {}));

// Per-project slides keyed by uid: { uid: [{files, text}, ...] | null (loading) }
const rawSlides = ref({});

// Returns the resolved slides array for a uid; empty array when loading/absent
const projectSlides = (uid) => rawSlides.value[uid] || [];

// Current navigation position
const currentProjectIdx = ref(0);
const currentSlideIdx   = ref(0);

const currentProject = computed(() => projects.value[currentProjectIdx.value]);

const totalSlides = computed(() => {
  const uid = currentProject.value?.uid;
  return uid ? (rawSlides.value[uid]?.length ?? 0) : 0;
});

// Fetch and cache the image blocks for a project
const fetchProject = async (uid) => {
  if (!uid) return;
  // hasOwnProperty check: null means in-progress, array means done
  if (Object.prototype.hasOwnProperty.call(rawSlides.value, uid)) return;
  // Reserve slot to prevent duplicate fetches
  rawSlides.value = { ...rawSlides.value, [uid]: null };
  try {
    const res = await axios.post('/api/cachedKQL', {
      select: {
        page: {
          query: `page("index/${uid}")`,
          select: {
            blocks: {
              query: 'page.blocks.toBlocks',
              select: {
                id: true,
                type: true,
                content: true,
                files: {
                  query: 'block.image.toFiles',
                  select: {
                    filename: true,
                    url: true,
                    width:  'file.width()',
                    height: 'file.height()',
                    srcset: 'file.srcset([250, 450, 800, 1440, 2880])',
                  }
                }
              }
            },
            images: {
              query: 'page.images',
              select: {
                filename: true,
                url: true,
                width:  'file.width()',
                height: 'file.height()',
                srcset: 'file.srcset([250, 450, 800, 1440, 2880])',
              }
            }
          }
        }
      }
    });
    const blocks = res.data?.result?.page?.blocks || [];
    // Build slide objects from Kirby 4 blocks; fall back to page.images for old-format content
    let slides = blocks
      .map(block => {
        if (block.type === 'image') {
          return { files: block.files || [], text: block.content?.caption || '' };
        }
        return null;
      })
      .filter(s => s && s.files.length > 0);
    if (!slides.length) {
      const images = res.data?.result?.page?.images || [];
      slides = images.map(img => ({ files: [img], text: '' }));
    }
    rawSlides.value = { ...rawSlides.value, [uid]: slides };
  } catch (e) {
    console.warn('[Home] Failed to fetch project slides:', uid, e);
    rawSlides.value = { ...rawSlides.value, [uid]: [] };
  }
};

// Preload images for a single slide
const preloadSlide = (uid, slideIdx) => {
  const slides = rawSlides.value[uid];
  if (!Array.isArray(slides)) return;
  const slide = slides[slideIdx];
  slide?.files?.forEach(f => { if (f.url) { const img = new Image(); img.src = f.url; } });
};

// Fetch current project + next one eagerly; rest on demand
const fetchAround = (idx) => {
  const list = projects.value;
  if (!list.length) return;
  fetchProject(list[idx]?.uid);
  fetchProject(list[(idx + 1) % list.length]?.uid);
};

watch(projects, () => fetchAround(currentProjectIdx.value), { immediate: true });
watch(currentProjectIdx, (idx) => fetchAround(idx));

// After rawSlides changes, preload the slide after the current one
watch(rawSlides, () => {
  const uid = currentProject.value?.uid;
  if (uid) preloadSlide(uid, currentSlideIdx.value + 1);
});

// Preload the next slide whenever position changes
watch(currentSlideIdx, (si) => {
  const uid = currentProject.value?.uid;
  if (uid) preloadSlide(uid, si + 1);
});

// Navigation — cycle slides across all projects -----------

const goNext = () => {
  const list   = projects.value;
  if (!list.length) return;
  const uid    = currentProject.value?.uid;
  const slides = Array.isArray(rawSlides.value[uid]) ? rawSlides.value[uid] : [];
  if (currentSlideIdx.value < slides.length - 1) {
    currentSlideIdx.value++;
  } else {
    currentProjectIdx.value = (currentProjectIdx.value + 1) % list.length;
    currentSlideIdx.value = 0;
  }
};

const goPrev = () => {
  const list = projects.value;
  if (!list.length) return;
  if (currentSlideIdx.value > 0) {
    currentSlideIdx.value--;
  } else {
    const prevIdx = (currentProjectIdx.value - 1 + list.length) % list.length;
    currentProjectIdx.value = prevIdx;
    const prevUid    = list[prevIdx]?.uid;
    const prevSlides = Array.isArray(rawSlides.value[prevUid]) ? rawSlides.value[prevUid] : [];
    currentSlideIdx.value = Math.max(0, prevSlides.length - 1);
  }
};

// Touch handling -----------------------------------------------------------
// .prevent on touchend stops the browser from synthesising a click; we
// handle taps and swipes here directly instead.

let touchStartX = 0;
const SWIPE_THRESHOLD = 40; // px

const onTouchStart = (e) => {
  touchStartX = e.touches[0].pageX;
};

const onTouchEnd = (e) => {
  const endX = e.changedTouches[0].pageX;
  const dx   = endX - touchStartX;
  if (Math.abs(dx) > SWIPE_THRESHOLD) {
    // Swipe gesture: left = next, right = prev
    if (dx < 0) goNext(); else goPrev();
  } else {
    // Tap: left half = prev, right half = next
    if (endX >= window.innerWidth / 2) goNext(); else goPrev();
  }
};
</script>

<style scoped>
/* Make the li a flex container so home-slide can stretch/grow within it,
   and the fallback cover img is centred the same way */
.slides li {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Fallback cover image (shown while slide data loads) */
.slides li > img {
  max-height: 75vh;
  max-width: 90vw;
  object-fit: contain;
  display: block;
}

/* Per-slide inner container: fills the li and centres the image */
.home-slide {
  flex-grow: 1;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
}

.home-slide img {
  max-height: 75vh;
  max-width: 90vw;
  object-fit: contain;
  display: block;
}

/* Project title: bottom left — mirrors the nav margin */
.home-title {
  position: fixed;
  left: var(--page-margin, 32px);
  bottom: var(--page-margin, 28px);
  margin: 0;
  z-index: 5;
  pointer-events: none;
}

/* Slide counter: bottom right — upright (not italic) */
.home-counter {
  position: fixed;
  right: var(--page-margin, 32px);
  bottom: var(--page-margin, 28px);
  margin: 0;
  z-index: 5;
  pointer-events: none;
  font-style: normal;
  text-align: right;
}

@media only screen and (max-width: 700px) {
  .home-title {
    left: var(--page-margin, 16px);
    bottom: var(--page-margin, 16px);
  }

  .home-counter {
    right: var(--page-margin, 16px);
    bottom: var(--page-margin, 16px);
  }

  .home-slide img,
  .slides li > img {
    max-width: calc(100vw - (var(--page-margin, 16px) * 2));
  }
}
</style>
